import classNames from 'classnames'
import { type CSSProperties, useEffect, useRef, useState } from 'react'

import styles from './toggleText.module.scss'

export interface ToggleTextProps {
  options: string[]
  activeIndex: number
  onChange?: (index: number, value: string) => void
  wrapperClassName?: string
}

const ToggleText = ({ options, activeIndex, onChange, wrapperClassName }: ToggleTextProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([])
  const [indicatorStyle, setIndicatorStyle] = useState<CSSProperties>({})

  const handleOptionClick = (index: number) => {
    const selectedValue = options[index]
    if (selectedValue === undefined) return
    onChange?.(index, selectedValue)
  }

  useEffect(() => {
    const updateIndicatorPosition = () => {
      const containerEl = containerRef.current
      const activeOptionEl = optionRefs.current[activeIndex]
      if (!containerEl || !activeOptionEl) return

      const containerRect = containerEl.getBoundingClientRect()
      const activeOptionRect = activeOptionEl.getBoundingClientRect()

      setIndicatorStyle({
        left: `${activeOptionRect.left - containerRect.left}px`,
        top: `${activeOptionRect.top - containerRect.top}px`,
        width: `${activeOptionRect.width}px`,
        height: `${activeOptionRect.height}px`,
      })
    }

    updateIndicatorPosition()

    const resizeObserver = new ResizeObserver(() => {
      updateIndicatorPosition()
    })

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current)
    }

    optionRefs.current.forEach(optionRef => {
      if (optionRef) {
        resizeObserver.observe(optionRef)
      }
    })

    return () => {
      resizeObserver.disconnect()
    }
  }, [activeIndex, options])

  if (options.length < 2) return null

  return (
    <div ref={containerRef} className={classNames(styles.toggleText, wrapperClassName)}>
      <span className={styles.toggleText__indicator} style={indicatorStyle} aria-hidden />
      {options.map((option, index) => {
        const isActive = activeIndex === index
        return (
          <button
            key={option}
            ref={element => {
              optionRefs.current[index] = element
            }}
            type="button"
            className={classNames(styles.toggleText__option, isActive && styles.toggleText__optionActive)}
            onClick={() => {
              handleOptionClick(index)
            }}
            aria-pressed={isActive}
          >
            {option}
          </button>
        )
      })}
    </div>
  )
}

export default ToggleText
